import {useEffect, useRef} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import _ from 'lodash';
import {Button, Input, Spinner, Tooltip} from "@heroui/react";
import moment from 'moment';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import toast from 'react-hot-toast';
import {twMerge} from 'tailwind-merge';
import {useDebouncedCallback, useIntersection} from "@mantine/hooks";

const inputWrapper = 'min-h-12s !bg-white border-1 data-[focus=true]:!border-default data-[hover=true]:border-default shadow-none';

export default function History({speakers, form, query}) {
    const historyContainerRef = useRef(null);
    const {stop} = useGlobalAudioPlayer()
    const virtualSpeakers = useVirtualizer({
        count: _.size(speakers || []),
        getScrollElement: () => historyContainerRef.current,
        estimateSize: () => 70,
        gap: 8,
        overscan: 5,
    })

    const {ref, entry} = useIntersection({
        threshold: 0.2,
    });

    const downloadFile = async (filePath, fileName, extention = null) => {
        if (filePath) {
            await fetch(filePath)
                .then(res => res.blob())
                .then(async (blob) => {
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement('a');
                    anchor.href = url;
                    anchor.download = `${_.trim(_.truncate(fileName, {length: 10}))}.${extention}`;
                    document.body.appendChild(anchor);
                    anchor.click();
                    document.body.removeChild(anchor);
                    URL.revokeObjectURL(url); // Clean up the URL
                })
                .catch(err => console.error('Error downloading file:', err));
        }
    }


    const fetchNextPage = useDebouncedCallback(() => {
        entry?.isIntersecting && query.fetchNextPage()
    }, 1000)

    useEffect(() => {
        fetchNextPage()
    }, [entry?.isIntersecting]);

    return (
        <>
            <div className="w-full h-full min-h-full relative">
                <div className="mb-2 border-b border-slate-200/30 pb-2">
                    <Input
                        label=""
                        placeholder="Search here..."
                        labelPlacement="outside"
                        type="text"
                        {...form.getInputProps('search_in_history')}
                        classNames={{
                            inputWrapper,
                            label: 'text-slate-500 mb-10 top-5',
                        }}
                        endContent={form.values?.search_in_history && <Button
                            isIconOnly size="sm" radius="full"
                            variant="light" color="danger"
                            onPress={() => form.setFieldValue('search_in_history', '')}
                            children={<i className="hgi-stroke hgi-cancel-01"></i>}
                        />}
                    />
                </div>
                <div
                    ref={historyContainerRef}
                    className="relative min-h-40 py-3 overflow-y-auto h-dvh max-h-[70dvh]">
                    {_.size(virtualSpeakers.getVirtualItems()) >= 1 ? (
                            <>
                                {virtualSpeakers.getVirtualItems()?.map((item) => {
                                    const itemData = speakers[item.index] || {};
                                    return (
                                        <div
                                            key={item.index}
                                            style={{
                                                transform: `translateY(${item.start}px)`,
                                                minHeight: `${item.size}px`,
                                            }}
                                            onDoubleClick={async () => {
                                                if (_.isEmpty(itemData?.voice_name)) {
                                                    toast.error('Not available to Regenerate');
                                                    return;
                                                }

                                                form.setFieldValue('language', itemData?.language)
                                                form.setFieldValue('text', itemData?.text)
                                                form.setFieldValue('current_audio', null)
                                                stop()
                                                form.setFieldValue('current_audio', {
                                                    name: itemData?.text,
                                                    src: itemData?.audio_file
                                                })
                                            }}
                                            className={twMerge([
                                                "absolute top-0 h-auto left-0 select-none w-full flex-nowrap overflow-x-hidden group cursor-pointer bg-white py-2 px-3 rounded-20 flex gap-3 items-center transition-all duration-300",
                                                form.values?.current_audio?.src == itemData?.audio_file ? 'bg-default-50' : 'bg-white',
                                            ])}
                                        >
                                            {/*current_audio*/}
                                            <div className="w-fit">
                                                <Button
                                                    onPress={() => {
                                                        //form.setFieldValue('text', itemData?.text)
                                                        form.setFieldValue('current_audio', null)
                                                        stop()
                                                        form.setFieldValue('current_audio', {
                                                            name: itemData?.text,
                                                            src: new Date().toISOString(),
                                                            is_playing: false,
                                                        })
                                                        setTimeout(() => {
                                                            form.setFieldValue('current_audio', {
                                                                name: itemData?.text,
                                                                src: itemData?.audio_file,
                                                                is_playing: true,
                                                            })
                                                        }, 300)
                                                    }}
                                                    isIconOnly radius="full" className="bg-default-200 text-black">
                                                    <i className={`hgi-stroke ${form.values?.current_audio?.src == itemData?.audio_file && form.values?.current_audio?.is_playing ? 'hgi-pause' : 'hgi-play'} text-xl`}></i>
                                                </Button>
                                            </div>
                                            <div className="text-left w-full group-hover:w-[48%] transition-all truncate">
                                                <h3 className="text-slate-800 truncate">
                                                    {itemData?.text}
                                                </h3>
                                                <div className="flex gap-2 items-center truncate">
                                                    <Tooltip color="foreground" shadow="lg" showArrow
                                                             placement="bottom-start"
                                                             content={moment(itemData?.created_at).utc(true).format('MMM DD, YYYY hh:m A')}
                                                             delay={300}>
                                                        <p className="text-small text-slate-500">{moment(itemData?.created_at).utc(true).fromNow()}</p>
                                                    </Tooltip>
                                                    <div className="w-2 h-2 bg-default rounded-full"/>
                                                    <p className="text-small text-slate-500">mp3</p>
                                                </div>
                                            </div>
                                            <div
                                                className="absolute top-0 right-0 px-3 flex gap-2 bg-white translate-x-32 group-hover:translate-x-0 transition-all duration-300 justify-end items-center h-full">
                                                <Tooltip
                                                    content={`${_.isEmpty(itemData?.voice_name) ? 'Not available to ' : ''} Regenerate`}
                                                    delay={300}>
                                                    <Button
                                                        isIconOnly size="sm"
                                                        className={twMerge([
                                                            "text-default-800 bg-default-50",
                                                            _.isEmpty(itemData?.voice_name) && 'opacity-50'
                                                        ])}
                                                        variant="light"
                                                        onPress={async () => {
                                                            if (_.isEmpty(itemData?.voice_name)) return;
                                                            form.setFieldValue('current_audio', null)
                                                            let speedInput = _.toNumber((itemData?.speed || '').replace(/[+%]/g, ''))
                                                            let speed = _.round(_.toNumber(_.toString(speedInput / 100 + 1).replace(/[+\-%]/g, '')), 2)
                                                            form.setValues(_.merge(form.values, {
                                                                ...itemData,
                                                                model: itemData?.voice_model,
                                                                speed: speed == 0 ? 1 : speed,
                                                                pitch: _.toNumber(itemData?.pitch),
                                                                volume: _.toNumber(itemData?.volume),
                                                                stability: _.toNumber(itemData?.stability),
                                                            }))

                                                            stop()
                                                        }}
                                                    >
                                                        <i className="hgi-stroke hgi-copy-02 text-xl"></i>
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip content="Share" delay={300}>
                                                    <Button
                                                        isIconOnly size="sm"
                                                        className="text-default-800 bg-default-50"
                                                        variant="light"
                                                        onPress={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(itemData?.audio_file || '');
                                                                toast.success('Audio file link copied to clipboard');
                                                            } catch (err) {
                                                                console.error('Failed to copy audio file link: ', err);
                                                                toast.error('Failed to copy link!');
                                                            }
                                                        }}
                                                    >
                                                        <i className="hgi-stroke hgi-copy-link text-xl"></i>
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip content="Download" delay={300}>
                                                    <Button
                                                        isIconOnly size="sm"
                                                        variant="light"
                                                        onPress={() => downloadFile(itemData?.audio_file, itemData?.text, 'mp3')}
                                                        className="text-default-800 bg-default-50"
                                                    >
                                                        <i className="hgi hgi-stroke hgi-download-05 text-xl"></i>
                                                    </Button>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    )
                                })}
                                {query?.hasNextPage && (
                                    <div
                                        ref={ref}
                                        key={_.size(speakers)}
                                        style={{
                                            transform: `translateY(${_.size(speakers) * 78}px)`,
                                            minHeight: `70px`,
                                        }}
                                        className={twMerge([
                                            "absolute top-0 h-auto left-0 select-none w-full flex-nowrap overflow-x-hidden group cursor-pointer py-2 px-3 rounded-20 flex gap-3 items-center justify-center transition-all duration-300",
                                        ])}
                                    >
                                        <Spinner size="md" radius="lg"/>
                                    </div>
                                )}
                            </>
                        ) :
                        <div className="w-full h-full flex justify-center items-center">
                            No Content Available
                        </div>
                    }

                </div>
            </div>
        </>
    );
}
